// Port of the LIS Delphi lisSmartAnsiCompareText from
// C:\!work\LIS_DEV\Source\OldAlphaLib\Core\lisSysUtils.Compare.pas
// (with its private CompareNumericParts helper).
//
// Digit runs discard leading zeros and compare by significant length, then
// lexicographically, so arbitrarily large digit strings never overflow.
// Text runs use one private Russian collator: case-insensitive but
// accent-sensitive (е and ё stay distinct). Mixed run types compare the raw
// UTF-16 character codes, matching the source's ordinal branch. Whole-string
// equality returns zero before token processing, no input is trimmed, and the
// final tie-break compares the original total lengths.
//
// Exact Delphi/Windows collation tables are not reproduced; the approved
// Russian collation policy above is the contract for both environments.

const textCollator = new Intl.Collator("ru", {
  usage: "sort",
  sensitivity: "accent",
  numeric: false,
  ignorePunctuation: false,
});

function isDigitAt(value: string, index: number): boolean {
  const code = value.charCodeAt(index);
  return code >= 48 && code <= 57;
}

function compareNumericParts(left: string, right: string): number {
  const a = left.replace(/^0+/, "");
  const b = right.replace(/^0+/, "");
  if (a.length !== b.length) return Math.sign(a.length - b.length);
  return a < b ? -1 : a > b ? 1 : 0;
}

export function smartAnsiCompareText(left: string, right: string): number {
  if (textCollator.compare(left, right) === 0) return 0;

  let leftStart = 0;
  let rightStart = 0;
  while (true) {
    if (leftStart === left.length) {
      return rightStart === right.length
        ? Math.sign(left.length - right.length)
        : -1;
    }
    if (rightStart === right.length) return 1;

    const leftDigit = isDigitAt(left, leftStart);
    const rightDigit = isDigitAt(right, rightStart);
    if (leftDigit !== rightDigit) {
      return Math.sign(
        left.charCodeAt(leftStart) - right.charCodeAt(rightStart),
      );
    }

    let leftEnd = leftStart + 1;
    let rightEnd = rightStart + 1;
    while (leftEnd < left.length && isDigitAt(left, leftEnd) === leftDigit) {
      leftEnd += 1;
    }
    while (rightEnd < right.length && isDigitAt(right, rightEnd) === rightDigit) {
      rightEnd += 1;
    }

    const a = left.slice(leftStart, leftEnd);
    const b = right.slice(rightStart, rightEnd);
    const result = leftDigit
      ? compareNumericParts(a, b)
      : textCollator.compare(a, b);
    if (result !== 0) return Math.sign(result);
    leftStart = leftEnd;
    rightStart = rightEnd;
  }
}